import { faClock, faPenToSquare, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';

import AutomatedInsuranceOptionsForm, {
  type AutomatedInsuranceOptions
} from '@/components/AutomatedInsuranceOptionsForm';
import ManualVerificationForm from '@/components/ManualVerificationForm';
import { serviceCodeLabel } from '@/components/PayerResponse';
import { daysSinceVerified } from '@/components/VerificationFreshness';
import VerificationStatusPill from '@/components/VerificationStatusPill';
import AppContext from '@/contexts/appContext';
import useAutoReverifyInsurancePolicy from '@/hooks/useAutoReverifyInsurancePolicy';
import { insurancePolicySummariesQueryKey } from '@/hooks/useInsurancePolicySummaries';
import PersonContext from '@/pages/Person/PersonContext';
import { ExternalVerificationSource } from '@/types/externalVerification';
import {
  Button,
  Modal,
  SplitButton,
  TabSwitch,
  TabSwitchList,
  TabSwitchPanel,
  TabSwitchPanels,
  TabSwitchTab,
  VendorLogo,
  vendorDisplayName
} from '@/ui';
import { currencyInCentsFormatter } from '@/utils/currencies';
import { hasPermission, PEOPLE_RUN_VOB } from '@/utils/userPermission';

import './styles.css';
import type { Props } from './types';

export const defaultReverificationVendor = (
  configuredVendor: unknown
): ExternalVerificationSource => {
  const normalized = String(configuredVendor ?? '').toUpperCase();

  return Object.values(ExternalVerificationSource).includes(
    normalized as ExternalVerificationSource
  )
    ? (normalized as ExternalVerificationSource)
    : ExternalVerificationSource.STEDI;
};

const LastVerificationStrip = ({ policy }: { policy: Props['policy'] }) => {
  const lastVerification = policy.last_verification;

  if (!lastVerification) {
    return (
      <div className="rounded-md border border-uniform-gray-200 bg-uniform-gray-50 px-3 py-2.5 text-sm text-uniform-gray-500">
        No verification on file yet.
      </div>
    );
  }

  const days = daysSinceVerified(lastVerification.verified_at);
  const details = [
    lastVerification.verified_at &&
      new Date(lastVerification.verified_at).toLocaleDateString('en-US'),
    days != null && `${days} days ago`,
    lastVerification.deductible_remaining != null &&
      `${currencyInCentsFormatter(lastVerification.deductible_remaining)} deductible left`,
    lastVerification.estimated_monthly_cost_in_cents != null &&
      `${currencyInCentsFormatter(lastVerification.estimated_monthly_cost_in_cents)}/mo`
  ].filter(Boolean);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-uniform-gray-200 bg-uniform-gray-50 px-3 py-2.5 text-sm">
      <span className="text-uniform-gray-500">Last verification</span>
      <VerificationStatusPill status={lastVerification.status} />
      <span>{details.join(' · ')}</span>
    </div>
  );
};

const NewVerificationModal = ({
  policy,
  open,
  onClose,
  initialTab = 'automated',
  initialShowOptions = false,
  onAutomatedRunStarted
}: Props) => {
  const { addMessage, config, permissions } = useContext(AppContext);
  const {
    person: { id: personId },
    refetch: refetchPerson
  } = useContext(PersonContext);
  const queryClient = useQueryClient();

  const canRunVob = hasPermission({ permissions, requiredPermission: PEOPLE_RUN_VOB });

  const defaultOptions = useMemo<AutomatedInsuranceOptions>(
    () => ({
      vendor: defaultReverificationVendor(config?.reverification_vendor),
      serviceCode: policy.last_verification?.service_code ?? '30',
      includeSsn: false
    }),
    [config?.reverification_vendor, policy.last_verification?.service_code]
  );

  const [selectedTab, setSelectedTab] = useState(initialTab === 'manual' || !canRunVob ? 1 : 0);
  const [isShowingOptions, setIsShowingOptions] = useState(initialShowOptions);
  const [options, setOptions] = useState(defaultOptions);
  const [pendingRun, setPendingRun] = useState<AutomatedInsuranceOptions | null>(null);

  const { mutateAsync: runAutomatedVerification, isPending: isStartingRun } =
    useAutoReverifyInsurancePolicy();

  const confirmRun = async () => {
    if (!pendingRun || !policy.id) return;

    try {
      await runAutomatedVerification({
        policyId: policy.id,
        data: {
          flags: { include_ssn: pendingRun.includeSsn },
          vendor: pendingRun.vendor,
          service_code: pendingRun.serviceCode
        }
      });
      queryClient.invalidateQueries({ queryKey: insurancePolicySummariesQueryKey(personId) });
      refetchPerson();
      onAutomatedRunStarted?.(pendingRun);
      setPendingRun(null);
      onClose();
    } catch (error) {
      setPendingRun(null);
      addMessage('error', `Failed to start Albert. ${error}`);
    }
  };

  const subtitle = [policy.carrier, policy.priority_type, policy.subscriber_id]
    .filter(Boolean)
    .join(' · ');

  const automatedBody = isShowingOptions ? (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => setIsShowingOptions(false)}
        className="self-start text-sm font-medium text-blue-700">
        ← Back to defaults
      </button>
      <AutomatedInsuranceOptionsForm
        value={options}
        onChange={setOptions}
        serviceCodeHint="Defaults to the last verification's code, else 30"
      />
      <div className="flex justify-end gap-3">
        <Button variant="outline" color="neutral" onClick={onClose}>
          Cancel
        </Button>
        <Button color="primary" onClick={() => setPendingRun(options)}>
          Run Albert with these options
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-uniform-gray-700">
        Albert checks eligibility with the payer and records the verification for you.
      </p>
      <div className="flex items-center justify-between gap-3 rounded-md border border-uniform-gray-300 px-3 py-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-uniform-gray-500">
            Runs with
          </span>
          <span className="inline-flex flex-wrap items-center gap-1.5 text-sm font-medium">
            <VendorLogo source={options.vendor} size="sm" showName />
            {`· ${serviceCodeLabel(options.serviceCode, ' – ')} · SSN ${options.includeSsn ? 'included' : 'not included'}`}
          </span>
          <span className="text-xs text-uniform-gray-500">
            Vendor from the Reverification Vendor setting · service code from the last verification
            (30 if none)
          </span>
        </div>
        <button
          type="button"
          onClick={() => setIsShowingOptions(true)}
          className="shrink-0 text-sm font-medium text-blue-700">
          Change
        </button>
      </div>
      <div className="flex items-start gap-2 text-sm text-uniform-gray-500">
        <FontAwesomeIcon icon={faClock} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Usually 1–2 minutes, up to 5. You can close this window: the card shows progress and
          you&apos;ll get a notification when it&apos;s done.
        </span>
      </div>
      <div className="flex justify-end gap-3">
        <Button variant="outline" color="neutral" onClick={onClose}>
          Cancel
        </Button>
        <SplitButton
          onClick={() => setPendingRun(options)}
          leftIcon={faRobot}
          menuLabel="More ways to run Albert"
          items={[
            {
              label: 'Run with options…',
              description: 'Choose vendor, service code and SSN use',
              onClick: () => setIsShowingOptions(true)
            }
          ]}>
          Run Albert
        </SplitButton>
      </div>
    </div>
  );

  const manualBody = (
    <ManualVerificationForm policy={policy} onSaved={onClose} onCancel={onClose} />
  );

  return (
    <>
      <Modal
        open={open && !pendingRun}
        onClose={onClose}
        size="lg"
        title="New verification"
        description={subtitle}>
        <div className="flex flex-col gap-4">
          <LastVerificationStrip policy={policy} />
          {canRunVob ? (
            <TabSwitch
              variant="pills"
              selectedIndex={selectedTab}
              onChange={(index) => setSelectedTab(index)}>
              <TabSwitchList>
                <TabSwitchTab icon={faRobot}>Albert (automatic)</TabSwitchTab>
                <TabSwitchTab icon={faPenToSquare}>Manual entry</TabSwitchTab>
              </TabSwitchList>
              <TabSwitchPanels>
                <TabSwitchPanel>{automatedBody}</TabSwitchPanel>
                <TabSwitchPanel>{manualBody}</TabSwitchPanel>
              </TabSwitchPanels>
            </TabSwitch>
          ) : (
            manualBody
          )}
        </div>
      </Modal>

      <Modal
        open={!!pendingRun}
        onClose={() => setPendingRun(null)}
        role="alertdialog"
        size="sm"
        restrictCloseToButton
        icon={<FontAwesomeIcon icon={faRobot} className="h-5 w-5 text-uniform-gray-600" />}
        title={`Run Albert on ${policy.carrier ?? 'this policy'}?`}
        description={
          pendingRun &&
          `Albert will check ${policy.subscriber_id ?? 'this policy'} with ${vendorDisplayName(pendingRun.vendor)} (service code ${pendingRun.serviceCode}, SSN ${pendingRun.includeSsn ? 'included' : 'not included'}) and record a new verification. Usually 1–2 minutes.`
        }
        secondaryAction={{
          label: isShowingOptions ? 'Back' : 'Cancel',
          onClick: () => setPendingRun(null)
        }}
        primaryAction={{ label: 'Run Albert', onClick: confirmRun, isLoading: isStartingRun }}
      />
    </>
  );
};

export default React.memo(NewVerificationModal);
